import { Ref, computed, ref } from 'vue';

import { screenMobile } from '@tallo/design-system';

export type ListView = 'cards' | 'table';

/** The lists that offer cards or rows; each keeps a choice of its own */
export type ListViewList = 'properties' | 'building-units';

// The properties list keeps the key it had before lists got their own, so a saved choice carries over
const storageKeys: Record<ListViewList, string> = {
  properties: 'list-view',
  'building-units': 'list-view:building-units',
};

/** One ref per list, so the toggle and the list it stands beside read the same choice */
const views = new Map<ListViewList, Ref<ListView>>();

// Browser storage can be missing or blocked (private windows, cleared site data); the list works without it
function readView(list: ListViewList): ListView {
  try {
    return localStorage.getItem(storageKeys[list]) === 'table' ? 'table' : 'cards';
  } catch {
    return 'cards';
  }
}

/** How someone likes to read one list, kept for this browser; a phone always gets cards, all that fits it */
export function useListView(list: ListViewList) {
  const view = views.get(list) ?? ref<ListView>('cards');
  views.set(list, view);
  // Read again for each list that opens: what the browser holds is the truth, wherever it was set
  view.value = readView(list);
  const showTable = computed(() => view.value === 'table' && !screenMobile.value);

  function setView(next: ListView) {
    view.value = next;

    try {
      localStorage.setItem(storageKeys[list], next);
    } catch {
      // Nothing to keep it in; the cards come back on the next visit
    }
  }

  return { view, showTable, setView };
}
